import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router";

import { ROUTES } from "@/app/routes";
import { useSession } from "@/core/auth/session";
import { ThemeSwitch } from "@/core/theme/ThemeSwitch";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { EnvironmentTag } from "@/ui/environment-tag";
import { LogoMark } from "@/ui/logo";

import { GoogleIcon } from "./GoogleIcon";
import { signInMessage } from "./sign-in-message";

const COPY = {
  signIn: {
    title: "Sign in",
    lead: "Use the account your brand invited.",
    submit: "Sign in",
    switchText: "New to Brocoly?",
    switchLink: "Create an account",
  },
  create: {
    title: "Create an account",
    lead: "Use the email your brand invited. We'll send a link to verify it.",
    submit: "Create account",
    switchText: "Already have an account?",
    switchLink: "Sign in",
  },
} as const;

/** Sign-in, or (mode "create") a new email account for someone invited with an address that isn't Google's. */
export function SignInPage({ mode = "signIn" }: { mode?: "signIn" | "create" }) {
  const copy = COPY[mode];
  const session = useSession();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState<"google" | "email" | "reset" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const next = new URLSearchParams(location.search).get("next");
  if (session.status === "signed-in") {
    return <Navigate to={next?.startsWith("/") && !next.startsWith("//") ? next : ROUTES.start} replace />;
  }

  const run = async (kind: "google" | "email", action: () => Promise<void>) => {
    setPending(kind);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (failure) {
      setError(signInMessage(failure));
    } finally {
      setPending(null);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void run("email", () =>
      mode === "create"
        ? session.createAccount(email.trim(), password)
        : session.signInWithEmail(email.trim(), password),
    );
  };

  const onForgot = async () => {
    if (!email.trim()) {
      setError("Type your email above first, then choose “Forgot?”.");
      return;
    }
    setPending("reset");
    setError(null);
    try {
      await session.sendPasswordReset(email.trim());
      setNotice(`If ${email.trim()} has an account, a reset link is on its way.`);
    } catch (failure) {
      setError(signInMessage(failure));
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="grid min-h-dvh grid-cols-1 lg:grid-cols-2">
      <section className="hidden flex-col justify-between bg-brand-deep p-14 text-white lg:flex">
        <div className="flex items-center gap-3">
          <LogoMark tile />
          <span className="text-[22px] font-extrabold">Brocoly Partner</span>
          <EnvironmentTag />
        </div>
        <div className="flex max-w-[460px] flex-col gap-4">
          <p className="text-[44px] leading-[1.1] font-extrabold tracking-tight">
            Your kitchen, every subscriber, one place.
          </p>
          <p className="text-[17px] leading-relaxed text-brand-mist">
            Menus, today's deliveries and your customers' plans, for your whole team.
          </p>
        </div>
        <span className="text-[13px] text-brand-mist-2">Partner portal · for brand teams on Brocoly</span>
      </section>

      <section className="flex flex-col bg-surface">
        <div className="flex items-center justify-between gap-3 px-6 py-4 lg:justify-end lg:px-4">
          <div className="flex min-w-0 items-center gap-3 lg:hidden">
            <LogoMark />
            <span className="truncate text-lg font-extrabold">Brocoly Partner</span>
            <EnvironmentTag />
          </div>
          <ThemeSwitch />
        </div>
        <div className="flex grow items-center justify-center px-6 pb-16">
          <div className="flex w-full max-w-[400px] flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-[30px] font-extrabold tracking-tight">{copy.title}</h1>
              <p className="text-[15px] text-muted">{copy.lead}</p>
            </div>

            {session.status === "unavailable" && (
              <p role="alert" className="rounded-xl bg-warn-soft p-3 text-sm text-warn">
                Can't reach Brocoly right now.{" "}
                <button type="button" className="font-bold underline" onClick={session.retry}>
                  Try again
                </button>
              </p>
            )}

            <Button
              variant="secondary"
              size="lg"
              loading={pending === "google"}
              disabled={pending !== null}
              onClick={() => void run("google", session.signInWithGoogle)}
            >
              {pending !== "google" && <GoogleIcon />}
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 text-[13px] text-faint">
              <span className="h-px grow bg-border" />
              or with email
              <span className="h-px grow bg-border" />
            </div>

            <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
              <Field
                label="Email"
                type="email"
                autoComplete="username"
                inputMode="email"
                placeholder="you@yourkitchen.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Field
                label="Password"
                type="password"
                autoComplete={mode === "create" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                hint={
                  mode === "create"
                    ? "At least 8 characters, with upper- and lower-case letters, a number and a symbol."
                    : undefined
                }
                labelAside={
                  mode === "create" ? undefined : (
                    <button
                      type="button"
                      className="text-[13px] font-semibold text-link hover:underline disabled:opacity-60"
                      onClick={() => void onForgot()}
                      disabled={pending !== null}
                    >
                      Forgot?
                    </button>
                  )
                }
              />
              <Button
                type="submit"
                size="lg"
                loading={pending === "email"}
                disabled={pending !== null || !email || !password}
              >
                {copy.submit}
              </Button>
            </form>
            <p className="text-center text-sm text-muted">
              {copy.switchText}{" "}
              <Link
                to={{
                  pathname: mode === "create" ? ROUTES.signIn : ROUTES.createAccount,
                  search: location.search,
                }}
                className="font-bold"
              >
                {copy.switchLink}
              </Link>
            </p>

            {error && (
              <p role="alert" className="rounded-xl bg-danger-soft p-3 text-sm font-medium text-danger">
                {error}
              </p>
            )}
            {notice && (
              <p role="status" className="rounded-xl bg-primary-soft p-3 text-sm text-primary-soft-ink">
                {notice}
              </p>
            )}
            <p className="text-center text-[13px] text-muted">
              Using a shared device? Sign out when you're done.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
